import { useEffect, useRef } from "react";
import { pingMeSeen } from "./api-modules/me.js";

/**
 * Presence-ping hook. Fires `POST /v1/me/seen` to advance the user's
 * `last_seen_at` timestamp on the server. Consumed by the
 * messages-notify Lambda to decide whether a recipient is "offline"
 * and should get an SES email (default threshold: 2h since last seen).
 *
 * Cadence (designed for "active = recently here", not "active = real-time"):
 *   - Once on app mount (cheap; one extra UpdateItem per app load).
 *   - On `window.focus` events, IF the last ping was >60s ago. Avoids
 *     a spam burst when the user alt-tabs through several apps.
 *   - On a 5-minute `setInterval`, but only when `!document.hidden`.
 *     Background tabs don't ping.
 *
 * **Why client-rate-limit instead of server-rate-limit:** the API
 * endpoint is a single UpdateItem; calling it once a second isn't
 * costly, but it's also pointless. The hook gates locally so the
 * server doesn't need to. If two tabs are open from the same user
 * they'll both ping; the user row's `last_seen_at` is just the
 * latest write, which is correct semantics.
 *
 * **Errors are swallowed** — a transient 5xx during a ping should not
 * disrupt the SPA. The next ping will succeed; meanwhile the
 * `last_seen_at` simply stays stale. Worst case: an offline-email is
 * sent to a user who is actually present.
 */

const FOCUS_PING_COOLDOWN_MS = 60_000;
const INTERVAL_MS = 5 * 60_000;

export function usePresenceTracker(enabled: boolean): void {
  // Cross-render reference so focus-event-throttling and interval-
  // gating share the same "last fired at" timestamp without forcing
  // re-renders on every ping.
  const lastFiredRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    const fire = () => {
      lastFiredRef.current = Date.now();
      pingMeSeen().catch(() => {
        // Swallow; presence is best-effort.
      });
    };

    // Initial mount ping.
    fire();

    const onFocus = () => {
      if (Date.now() - lastFiredRef.current >= FOCUS_PING_COOLDOWN_MS) {
        fire();
      }
    };
    window.addEventListener("focus", onFocus);

    const intervalId = window.setInterval(() => {
      if (!document.hidden) fire();
    }, INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(intervalId);
    };
  }, [enabled]);
}
